/**
 * Pairing flow against the Worker's HTTP API.
 *
 * The QR carries a short-lived token only. It buys the right to *ask*; the PC
 * still has to approve, and the persistent secret is delivered once over TLS
 * afterwards.
 */

import type {
  DeviceCredential,
  PairClaimResponse,
  PairStatusResponse,
} from '../../shared/signaling/protocol.ts';

export class PairingError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'PairingError';
  }
}

/** Opens a claim. Returns the claim id the caller then polls. */
export async function claim(
  endpoint: string,
  installId: string,
  pairingToken: string,
  deviceName: string,
  devicePublicId: string,
): Promise<PairClaimResponse> {
  const res = await fetch(`${endpoint}/pair/claim?install=${encodeURIComponent(installId)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ pairingToken, deviceName, devicePublicId }),
  });

  if (!res.ok) {
    const detail = await readError(res);
    throw new PairingError(
      res.status === 401
        ? "Ce code d'appairage est expiré ou déjà utilisé. Générez-en un nouveau sur le PC."
        : detail,
      String(res.status),
    );
  }
  return (await res.json()) as PairClaimResponse;
}

/**
 * Polls until the PC decides. Resolves with the credential on approval, or
 * throws with the reason — never returns a half-state the caller must guess at.
 */
export async function awaitApproval(
  endpoint: string,
  installId: string,
  claimId: string,
  signal: AbortSignal,
  onTick?: (secondsWaited: number) => void,
): Promise<DeviceCredential> {
  const started = Date.now();

  for (;;) {
    if (signal.aborted) throw new PairingError('Appairage annulé.', 'aborted');

    const res = await fetch(
      `${endpoint}/pair/status?install=${encodeURIComponent(installId)}&claim=${encodeURIComponent(claimId)}`,
      { signal },
    );
    if (!res.ok) throw new PairingError(await readError(res), String(res.status));

    const body = (await res.json()) as PairStatusResponse;
    if (body.state === 'approved') {
      if (!body.credential) {
        /* Approved but the secret was already collected: this browser lost it,
           so a fresh pairing is the only correct path. */
        throw new PairingError(
          'Cet appairage a déjà été utilisé par un autre navigateur. Générez un nouveau code.',
          'already-collected',
        );
      }
      return body.credential;
    }
    if (body.state === 'denied') throw new PairingError('Le PC a refusé cet appareil.', 'denied');
    if (body.state === 'expired') {
      throw new PairingError("La demande d'appairage a expiré.", 'expired');
    }

    onTick?.(Math.round((Date.now() - started) / 1000));
    await sleep(1200, signal);
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string; error?: string };
    return body.message ?? body.error ?? `Erreur ${res.status}`;
  } catch {
    return `Erreur ${res.status}`;
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new PairingError('Appairage annulé.', 'aborted'));
      },
      { once: true },
    );
  });
}
